import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@fluentui/react-components';
import { Laptop, Trash2, Users } from 'lucide-react';

import CampaignActivity from '../components/CampaignActivity';
import DeviceReadinessTiles from '../components/DeviceReadinessTiles';
import ReadinessSummaryPanel from '../components/ReadinessSummaryPanel';
import DeleteMigrationDialog from '../components/DeleteMigrationDialog';
import GroupLabel from '../components/GroupLabel';
import MigrationProgressPanel, { isOperationalCampaign } from '../components/MigrationProgressPanel';
import RelativeTime from '../components/RelativeTime';
import { CAMPAIGN_WAVE_ANCHOR_ID } from '../components/CampaignContextColumn';
import { Fact, MetricCard, SectionPanel } from '../components/abPrimitives';
import { deriveMappingChipStatus } from '../components/identity/MappingStatusChip';
import { useIdentitySuggestions, useSuggestionRows } from '../hooks/useIdentitySuggestions';
import { useMigrationDetailQuery } from '../hooks/useMigrationDetail';
import { deriveCampaignLifecycle } from '../lib/campaignLifecycle';
import { migrationTypeLabel } from '../lib/vocabulary';
import { ACCENT, IDENTIFIER, NUMERIC, STATUS, SURFACE, TEXT } from '../theme/tokens';

// The campaign Overview tab (FD-492, mockup 1; retiled by FD-502): two tiles
// and the next step on one row, then the campaign's own record, its activity
// trail, and the danger zone.
//
// ── WHAT MOVED OUT, AND WHERE IT WENT ──────────────────────────────────────
//
// The FD-127 device-status breakdown and identity-reconciliation breakdown are
// NOT here any more, and this is the one change on this page a reader is owed a
// reason for. R-FD-051 puts the per-status device tally in FD-498's in-progress
// view — the operational surface that replaces this one WHILE DEVICES ARE
// FLYING — and the per-user mapping tally on the Identities tab, which is the
// workspace that can act on it. What survives here is the count an operator
// reads at a glance, on a tile, plus the completion tally in its caption: a
// campaign that is not migrating does not need a five-row histogram of a state
// it is not in.
//
// `WavePanel` and `WavePackagePanel` moved one route level up in FD-491, into
// the shell's context column, where they render on all four tabs. They are NOT
// re-mounted here: they mint, revoke and abort a real tenant-join credential,
// and two sets of those buttons in front of one operator is the failure
// `MigrationLayoutShell.test.tsx` asserts against.
//
// ── EVERY NUMBER COMES FROM QUERIES THE PAGE ALREADY MAKES ─────────────────
//
// The tiles, the next step and the details panel are all derived from the
// migration-detail response the LAYOUT already fetched (one cache entry, FD-271)
// plus the identity-suggestions query this page has paged since FD-127. Recent
// activity adds exactly one O(1) partition query — see `CampaignActivity`, which
// carries the FD-ADR-043 D4 narrowing in full.

type DeviceStatusGroup = 'completed' | 'started' | 'other';

function classifyDevice(status: string, terminalOutcome: string | null | undefined): DeviceStatusGroup {
  const s = (status ?? '').trim().toLowerCase();
  const outcome = (terminalOutcome ?? '').trim().toLowerCase();
  if (s === 'completed' || s === 'complete' || outcome === 'completed') return 'completed';
  if (
    s === 'inprogress' ||
    s === 'in-progress' ||
    s === 'in progress' ||
    s === 'running' ||
    outcome !== ''
  ) {
    return 'started';
  }
  return 'other';
}

export default function MigrationOverviewPage() {
  const { migrationId } = useParams<{ migrationId: string }>();
  const query = useMigrationDetailQuery(migrationId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // FD-478 — the delete affordance. Dialog state is LOCAL rather than in the URL,
  // deliberately: the ADR-015 rule keeps *view* state in the URL so a filtered
  // page can be shared and survives a reload, and a shareable link that opens a
  // pre-armed destructive dialog is the opposite of what that rule is for.
  const [deleteOpen, setDeleteOpen] = useState(false);

  const migration = query.data?.migration;
  const devices = useMemo(() => query.data?.devices ?? [], [query.data]);

  const migrationType = migration?.migrationType;
  const isMappingType = migrationType === 'T2T' || migrationType === 'D2C';

  const suggestionsQuery = useIdentitySuggestions(migrationId, isMappingType);
  const { rows, totalUniqueUsers, devicesWithoutManifest } = useSuggestionRows(suggestionsQuery);

  // Page the suggestions to completion so the mapped tally reflects every unique
  // user, not just the first 50. The server total (`totalUniqueUsers`) is
  // accurate regardless — only the mapped count depends on all rows being loaded,
  // which is why the tile says "counting" rather than showing a partial figure.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = suggestionsQuery;
  useEffect(() => {
    if (isMappingType && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isMappingType, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const mappingCounts = useMemo(() => {
    const c = { mapped: 0, needsAttention: 0 };
    for (const row of rows) {
      const status = deriveMappingChipStatus(row);
      if (status === 'mapped') c.mapped += 1;
      else if (status === 'needsAttention') c.needsAttention += 1;
    }
    return c;
  }, [rows]);

  const deviceTally = useMemo(() => {
    let completed = 0;
    let started = 0;
    for (const d of devices) {
      const group = classifyDevice(d.status, d.terminalOutcome);
      if (group === 'completed') completed += 1;
      else if (group === 'started') started += 1;
    }
    return { completed, started };
  }, [devices]);

  if (!migration) return null;

  // THE SAME DERIVATION THE TOP-BAR CTA USES, from the same lib and the same
  // cached response — `MigrationLayout` calls `deriveCampaignLifecycle` on the
  // migration-detail query this page reads out of the cache. One rule, one
  // source: a second implementation of "what should the operator do next" is how
  // the card and the top bar come to disagree, and
  // `MigrationOverviewPage.test.tsx` asserts the two labels are identical rather
  // than trusting that they are.
  const lifecycle = deriveCampaignLifecycle(migration, devices);
  const nextAction = lifecycle.nextAction;

  const identityLoading =
    isMappingType &&
    (suggestionsQuery.isLoading ||
      suggestionsQuery.hasNextPage ||
      suggestionsQuery.isFetchingNextPage);

  return (
    <div className="flex flex-col">
      {/* FD-498, mockup 9 — the operational view, FIRST, because once devices are
          moving that is the headline and the campaign's own record is context.
          `isOperationalCampaign` is the whole trigger and it has two arms: a wave
          with devices under way, and a REVOKED wave (the FD-498 amendment — a
          finished campaign otherwise has its outcome distribution nowhere).

          IT IS ADDITIVE RATHER THAN A PAGE SWAP, and that is a deliberate
          reading of "the Overview tab swaps to an operational view". The four
          tiles below carry CAMPAIGN facts — wave state, package state, identity
          progress — which stay true and useful while a wave flies; this section
          carries DEVICE facts, which is what FD-492 removed and the amendment
          asks for back. Replacing the tile row would also mean editing FD-492's
          own assertions about them, which is how a green test gets weakened to
          fit a layout change. */}
      {isOperationalCampaign(lifecycle.currentIndex) && (
        <MigrationProgressPanel migration={migration} devices={devices} />
      )}

      {/* FD-502 — TWO TILES AND THE NEXT STEP, on one row.
          The row carried four: Devices assigned, Identities mapped, Wave
          status and Provisioning package. The last two are now the two panels
          at the top of the context column, on screen beside this row and on
          every other tab as well — and those panels are the surfaces that can
          ACT on what they report (activate, complete, abort, build, retry a
          receipt), where the tiles could only restate it. One fact, one
          rendering, and the one that carries the buttons wins.

          The "Not reported" vs "Not built" distinction the package tile
          existed to preserve travels with it: `WavePackagePanel` draws the
          same four states and `WavePackagePanel.test.tsx` asserts them, so
          nothing about that FD-269-class defect is now unguarded.

          THE GRID COLUMN COUNT FOLLOWS WHAT IS IN IT. Four columns with the
          next step spanning two; two columns when a finished campaign has no
          next step, so two tiles do not sit in a row built for four with half
          of it empty. Both class strings are written out in full because
          Tailwind scans for literals — an interpolated `xl:grid-cols-${n}`
          emits no CSS at all. */}
      <div
        data-testid="campaign-metrics"
        className={
          nextAction
            ? 'mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4'
            : 'mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2'
        }
      >
        <MetricCard
          label="Devices assigned"
          value={migration.deviceCount.toLocaleString()}
          caption={
            devices.length === 0
              ? 'None assigned yet'
              : `${deviceTally.completed} completed · ${deviceTally.started} under way`
          }
          icon={<Laptop size={18} strokeWidth={1.5} />}
        />

        <IdentityTile
          isMappingType={isMappingType}
          loading={identityLoading}
          errored={isMappingType && suggestionsQuery.isError}
          mapped={mappingCounts.mapped}
          needsAttention={mappingCounts.needsAttention}
          total={totalUniqueUsers}
          awaitingManifest={devicesWithoutManifest}
        />

        {/* The next step, IN the row rather than under it (FD-502). It renders
            NOTHING when `nextAction` is null, which is what a finished campaign
            returns — the same null the top bar honours. FD-490 typed it
            nullable to force exactly that: a card headed "Next step" on a
            campaign that has none is the console making something up.

            Still a <section>, and still led by its own "Next step" eyebrow: the
            card is scoped by both in `MigrationOverviewPage.test.tsx`, and the
            move is a layout change, not a change to what this card is. */}
        {nextAction && (
          <section
            className="flex flex-col rounded-lg border px-4 py-3.5 sm:col-span-2"
            style={{ borderColor: ACCENT.base, backgroundColor: SURFACE.base }}
          >
            <span
              className="inline-flex w-fit items-center rounded-full px-2.5 py-1 text-[11px] font-medium"
              style={{ backgroundColor: ACCENT.tint, color: ACCENT.pressed }}
            >
              Next step
            </span>
            <h2 className="mb-0 mt-2 text-[15px] font-semibold" style={{ color: TEXT.primary }}>
              {nextAction.label}
            </h2>
            <p
              className="mt-1 max-w-[60ch] text-[12px] leading-relaxed"
              style={{ color: TEXT.secondary }}
            >
              {nextAction.reason}
            </p>
            <div className="mt-3">
              <Button appearance="primary" onClick={() => navigate(nextAction.to)}>
                {nextAction.label}
              </Button>
            </div>
          </section>
        )}
        {/* NO PERCENTAGE DELTAS on any tile. `MetricCard` has no delta slot by
            construction (FD-ADR-043 D4): the mockups draw "↑ 12%" on every one,
            and nothing in the platform stores a previous value, so each of those
            figures would have to be invented. */}
      </div>

      {/* FD-503 — PREFLIGHT READINESS, moved here from the Devices tab (owner
          decision, 2026-09-27). It is a wave-level AGGREGATE — five verdict
          counts, the enforcement mode, the ruleset in force and the blocking
          reasons — and it was sitting on top of the per-device table, which is
          the one surface whose job is the rows. Monitoring the campaign is this
          page's job; acting on a device is that one's.

          GATED ON THE CAMPAIGN HAVING DEVICES, not on the wave being
          operational. Verdicts are stamped when a device submits its manifest,
          which happens during PREPARATION — before activation, and exactly when
          a held device is cheapest to fix. Gating this on `isOperationalCampaign`
          would hide readiness for the whole window in which it is most
          actionable.

          THE COUNTS ARE SUPPRESSED IN THE PANEL because the tiles above it are
          the same five numbers; everything else the panel carries is what the
          tiles cannot say — and the ENFORCEMENT SENTENCE in particular is never
          suppressible, because "nothing is blocked" and "blocking is switched
          off" look identical without it (FD-205).

          The held affordance is a LINK, not a second piece of filter state: it
          navigates to the Devices tab with `?held=1`, the same param that tab's
          readiness dropdown reads. */}
      {devices.length > 0 && (
        <SectionPanel title="Preflight readiness">
          <div className="flex flex-col gap-4">
            <DeviceReadinessTiles summary={migration.readinessSummary} devices={devices} />
            <ReadinessSummaryPanel
              migration={migration}
              showStatusCounts={false}
              heldFilterActive={false}
              onToggleHeldFilter={() =>
                navigate(
                  `/migrations/${encodeURIComponent(migration.migrationId)}/devices?held=1`,
                )
              }
            />
          </div>
        </SectionPanel>
      )}

      <SectionPanel title="Campaign details">
        {/* The TENANTS ARE DELIBERATELY NOT REPEATED HERE. The shell's header
            shows the conduit (source → destination) on every tab, and the
            context column links both records under "Related resources"
            (FD-491). A third copy on the same screen is noise, not detail. */}
        <dl className="m-0 grid grid-cols-1 gap-x-12 gap-y-4 sm:grid-cols-2">
          <Fact label="Name">{migration.migrationName}</Fact>
          <Fact label="Campaign ID">
            <span className="text-[11px]" style={IDENTIFIER}>
              {migration.migrationId}
            </span>
          </Fact>
          {/* Operators read Hybrid and Cloud; `D2C` / `T2T` stay on the wire
              (FD-ADR-031 §3). The mapping lives in `lib/vocabulary.ts` and
              nowhere else — an unrecognised value comes back verbatim. */}
          <Fact label="Type">{migrationTypeLabel(migration.migrationType)}</Fact>
          <Fact label="Wave">{migration.migrationWave}</Fact>
          <Fact label="Group">
            <GroupLabel
              migrationGroupId={migration.migrationGroupId}
              migrationGroupName={migration.migrationGroupName}
            />
          </Fact>
          {/* NO "Devices assigned" ROW. The tile above and the shell's header
              fact both carry that number already; a third copy on one screen is
              noise, and three renderings of one figure is how two of them come
              to disagree. */}
          <Fact label="Autopilot tag">
            {migration.autopilotGroupTag?.trim() ? (
              <span style={IDENTIFIER}>{migration.autopilotGroupTag}</span>
            ) : (
              // "None" rather than an em-dash: an untagged campaign is the
              // ORDINARY state (the source tag wins, and the campaign tag is
              // only the default for untagged devices — FD-ADR-025 §5 as
              // amended), and a dash would read as missing data.
              <span style={{ color: TEXT.secondary }}>None</span>
            )}
          </Fact>
          <Fact label="Created">
            <RelativeTime iso={migration.createdAt} />
          </Fact>
          <Fact label="Last updated">
            <RelativeTime iso={migration.updatedAt} />
          </Fact>
        </dl>
      </SectionPanel>

      <CampaignActivity migrationId={migration.migrationId} />

      {/* FD-478 (FD-ADR-044) — the delete, at the FOOT of the page and nowhere
          near the list. A delete on a campaigns-list row is one misclick away
          from the row above it, and the row above it belongs to someone else.
          It is also NOT in the header's `···` menu: that ADR chose
          detail-over-list precisely to make an irreversible act harder to reach,
          and two entry points to one is worse than one. */}
      <section className="mb-6">
        <h2 className="mb-2 mt-0 text-[15px] font-semibold" style={{ color: TEXT.primary }}>
          Danger zone
        </h2>
        <div
          className="rounded-lg p-5"
          style={{ border: `1px solid ${STATUS.errorTint}`, backgroundColor: SURFACE.base }}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-xl">
              <h3 className="m-0 text-[13px] font-semibold" style={{ color: TEXT.primary }}>
                Delete this campaign
              </h3>
              <p className="mt-1 text-[13px] leading-relaxed" style={{ color: TEXT.secondary }}>
                Removes the campaign, its devices, identity mappings, events, manifests and flight
                plans, and revokes its wave credential in the destination tenant. A salvaged device
                keeps its recovery password. This cannot be undone.
              </p>
            </div>
            <Button
              icon={<Trash2 size={16} strokeWidth={1.5} />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete campaign
            </Button>
          </div>
        </div>
      </section>

      {/* Kept MOUNTED and toggled open — a Fluent Dialog mounted already-open
          never enters the accessibility tree (FLUENT_UI_ADDENDUM). */}
      <DeleteMigrationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        migrationId={migration.migrationId}
        migrationName={migration.migrationName}
        /* FD-491 — the wave panel now lives in the shell's context column, one
           route level up, so a React ref cannot reach it. The panel carries a
           stable DOM id instead, and the constant is exported from the component
           that renders it so the two cannot drift into a silently dead scroll.
           `?.` is load-bearing: a viewport that has collapsed the context column
           still has the node, but a future layout that does not render it at all
           must degrade to no jump rather than to a crash — which is exactly what
           `onGoToWave`'s own doc comment asks for. */
        onGoToWave={() =>
          document
            .getElementById(CAMPAIGN_WAVE_ANCHOR_ID)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        onDeleted={() => {
          // The teardown is queued, so the row may survive a few more seconds —
          // but this page is about to be showing a campaign that is being
          // removed, so leave it rather than let it 404 under the operator.
          queryClient.invalidateQueries({ queryKey: ['migrations'] });
          queryClient.invalidateQueries({ queryKey: ['migration-groups'] });
          navigate('/migrations');
        }}
      />
    </div>
  );
}

/**
 * The identities tile, which must survive FOUR different absences without
 * claiming the same thing about any two of them (the FD-246 discipline):
 *
 *   * a migration type that does not reconcile identities at all
 *   * a suggestions feed that failed
 *   * a count that is still being paged — a PARTIAL mapped figure is a wrong
 *     figure, so it is not shown at all
 *   * a real zero, which is a real answer and renders as `0`
 */
function IdentityTile({
  isMappingType,
  loading,
  errored,
  mapped,
  needsAttention,
  total,
  awaitingManifest,
}: {
  isMappingType: boolean;
  loading: boolean;
  errored: boolean;
  mapped: number;
  needsAttention: number;
  total: number;
  awaitingManifest: number;
}) {
  const icon = <Users size={18} strokeWidth={1.5} />;

  if (!isMappingType) {
    return (
      <MetricCard
        label="Identities mapped"
        value="—"
        caption="This migration type does not reconcile identities."
        icon={icon}
      />
    );
  }
  if (errored) {
    return (
      <MetricCard
        label="Identities mapped"
        value="—"
        caption="Suggestions are temporarily unavailable."
        icon={icon}
        tone="warning"
      />
    );
  }
  if (loading) {
    return (
      <MetricCard
        label="Identities mapped"
        value="—"
        caption="Counting unique users…"
        icon={icon}
      />
    );
  }

  const caption =
    needsAttention > 0
      ? `${needsAttention} need${needsAttention === 1 ? 's' : ''} attention`
      : awaitingManifest > 0
        ? `${awaitingManifest} device${awaitingManifest === 1 ? '' : 's'} awaiting a manifest`
        : total === 0
          ? 'No users reported yet'
          : 'Every reported user is mapped';

  return (
    <MetricCard
      label="Identities mapped"
      value={
        <>
          {mapped.toLocaleString()}
          <span className="text-[15px] font-normal" style={{ ...NUMERIC, color: TEXT.secondary }}>
            {' / '}
            {total.toLocaleString()}
          </span>
        </>
      }
      caption={caption}
      icon={icon}
      tone={needsAttention > 0 ? 'warning' : undefined}
    />
  );
}
